import React, { useState, useEffect, useRef } from 'react';
import { X, Mic, Square, Play, Pause, Check, Music, AlertCircle } from 'lucide-react';
import { ref, push, set } from 'firebase/database';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { database, storage } from '../../firebase/config';
import useRecorder from '../../hooks/useRecorder';

export default function VoiceMemoRecorder({ isOpen, onClose, user, bandId, songs = [], isPremium, ideasCount = 0 }) {
  const {
    isRecording,
    isPaused,
    audioBlob,
    duration,
    error: recorderError,
    isSupported,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetRecording,
    getAudioURL
  } = useRecorder();

  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showSongSelect, setShowSongSelect] = useState(false);
  const [selectedSongId, setSelectedSongId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  // Free tier limit
  const FREE_IDEAS_LIMIT = 3;
  const canSaveIdea = isPremium || ideasCount < FREE_IDEAS_LIMIT;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setLabel('');
      setError('');
      setShowSongSelect(false);
      setSelectedSongId(null);
      resetRecording();
    }
  }, [isOpen, resetRecording]);

  // Update error from recorder
  useEffect(() => {
    if (recorderError) {
      setError(recorderError);
    }
  }, [recorderError]);

  // Create a stable audio URL when blob is ready
  const [audioURL, setAudioURL] = useState(null);

  useEffect(() => {
    if (audioBlob) {
      const url = URL.createObjectURL(audioBlob);
      setAudioURL(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setAudioURL(null);
    }
  }, [audioBlob]);

  // Handle audio playback end
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.onended = () => setIsPlaying(false);
    }
  }, [audioURL]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlayback = () => {
    if (!audioRef.current || !audioURL) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play()
        .then(() => setIsPlaying(true))
        .catch(err => {
          console.error('Playback failed:', err);
          setIsPlaying(false);
        });
    }
  };

  const handleStartRecording = async () => {
    setError('');
    const success = await startRecording();
    if (!success) {
      // Error is set by the hook
    }
  };

  const saveToIdeas = async () => {
    if (!audioBlob || !user || !bandId) return;

    if (!canSaveIdea) {
      setError(`Free accounts are limited to ${FREE_IDEAS_LIMIT} ideas. Convert ideas to songs or upgrade to Premium.`);
      return;
    }

    try {
      setSaving(true);
      setError('');

      // Generate filename
      const timestamp = Date.now();
      const extension = audioBlob.type.includes('mp4') ? 'mp4' : 'webm';
      const fileName = `idea-${timestamp}.${extension}`;
      const storagePath = `bands/${bandId}/ideas/${fileName}`;

      // Upload to Firebase Storage
      const fileRef = storageRef(storage, storagePath);
      const uploadTask = uploadBytesResumable(fileRef, audioBlob);

      const downloadURL = await new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          null,
          (error) => reject(error),
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          }
        );
      });

      // Save metadata to database
      const ideasRef = ref(database, `bands/${bandId}/ideas`);
      const newIdeaRef = push(ideasRef);
      
      await set(newIdeaRef, {
        fileName,
        fileURL: downloadURL,
        storagePath,
        duration,
        label: label.trim() || `Idea ${new Date().toLocaleDateString()}`,
        createdAt: new Date().toISOString(),
        createdBy: user.uid,
        createdByName: user.displayName || user.email,
        status: 'idea',
        linkedSongId: null
      });

      setSaving(false);
      onClose();
    } catch (err) {
      console.error('Failed to save idea:', err);
      setError('Failed to save: ' + err.message);
      setSaving(false);
    }
  };

  const addToSong = async () => {
    if (!audioBlob || !user || !bandId || !selectedSongId) return;

    try {
      setSaving(true);
      setError('');

      // Generate filename
      const timestamp = Date.now();
      const extension = audioBlob.type.includes('mp4') ? 'mp4' : 'webm';
      const fileName = `version-${timestamp}.${extension}`;
      const storagePath = `bands/${bandId}/audio/${selectedSongId}/${fileName}`;

      // Upload to Firebase Storage
      const fileRef = storageRef(storage, storagePath);
      const uploadTask = uploadBytesResumable(fileRef, audioBlob);

      const downloadURL = await new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          null,
          (error) => reject(error),
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          }
        );
      });

      // Add as new version to the song
      const versionRef = ref(database, `bands/${bandId}/songs/${selectedSongId}/versions`);
      const newVersionRef = push(versionRef);
      
      await set(newVersionRef, {
        fileName,
        fileURL: downloadURL,
        storagePath,
        uploadDate: new Date().toISOString(),
        fileSize: (audioBlob.size / 1024).toFixed(2),
        uploadedBy: user.uid,
        uploadedByName: user.displayName || user.email,
        notes: label.trim() ? `Recorded idea: ${label.trim()}` : 'Recorded idea'
      });

      // Update song's updatedAt
      const songRef = ref(database, `bands/${bandId}/songs/${selectedSongId}`);
      const { update } = await import('firebase/database');
      await update(songRef, { updatedAt: new Date().toISOString() });

      setSaving(false);
      onClose();
    } catch (err) {
      console.error('Failed to add to song:', err);
      setError('Failed to save: ' + err.message);
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Blurred backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={!isRecording && !saving ? onClose : undefined} />
      
      {/* Floating card */}
      <div className="relative w-full max-w-md card-3d rounded-card p-6 sm:p-8 text-white">
        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={isRecording || saving}
          className="absolute top-4 right-4 bg-transparent hover:bg-brand 
            disabled:opacity-50 transition p-2 rounded-full"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        <div className="text-center">
          {/* Error Display */}
          {error && (
            <div className="bg-red-900/50 rounded-card p-3 mb-6 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-100 text-sm text-left">{error}</p>
            </div>
          )}

          {/* Not Supported Warning */}
          {!isSupported && (
            <div className="bg-yellow-900/50 rounded-card p-4 mb-6">
              <p className="text-yellow-100 text-sm">
                Recording is not supported in this browser. Please try Chrome, Firefox, or Safari.
              </p>
            </div>
          )}

          {/* Recording State */}
          {!audioBlob ? (
            <>
              {/* Pulsing indicator during recording */}
              {isRecording && (
                <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center bg-red-500 animate-pulse">
                  <div className="w-6 h-6 bg-white rounded-full" />
                </div>
              )}

              {/* Timer */}
              <p className="text-4xl font-mono font-bold mb-8 text-white">
                {formatTime(duration)}
              </p>

              {/* Max duration warning */}
              {duration >= 110 && (
                <p className="text-yellow-400 text-sm mb-4">
                  Max recording time: 2 minutes
                </p>
              )}

              {/* Recording Controls */}
              <div className="flex justify-center gap-4">
                {!isRecording ? (
                  <button
                    onClick={handleStartRecording}
                    disabled={!isSupported}
                    className="bg-red-500 hover:bg-red-600 disabled:opacity-disabled 
                      disabled:cursor-not-allowed transition p-6 rounded-full"
                  >
                    <Mic className="w-8 h-8 text-white" />
                  </button>
                ) : (
                  <>
                    {isPaused ? (
                      <button
                        onClick={resumeRecording}
                        className="bg-green-500 hover:bg-green-600 transition p-4 rounded-full"
                      >
                        <Play className="w-6 h-6 text-white" />
                      </button>
                    ) : (
                      <button
                        onClick={pauseRecording}
                        className="bg-yellow-500 hover:bg-yellow-600 transition p-4 rounded-full"
                      >
                        <Pause className="w-6 h-6 text-white" />
                      </button>
                    )}
                    <button
                      onClick={stopRecording}
                      className="bg-transparent border border-surface-border-light hover:border-surface-border transition p-4 rounded-full"
                    >
                      <Square className="w-6 h-6 text-white" />
                    </button>
                  </>
                )}
              </div>

              {!isRecording && (
                <p className="text-text-subtle text-sm mt-6">
                  Tap to start recording
                </p>
              )}
            </>
          ) : (
            <>
              {/* Playback & Save State */}
              <div className="w-24 h-24 mx-auto mb-4 bg-green-500 rounded-full flex items-center justify-center">
                <Check className="w-12 h-12 text-white" />
              </div>

              <p className="text-xl font-semibold mb-2 text-white">Recording Complete!</p>
              <p className="text-text-subtle mb-6">{formatTime(duration)}</p>

              {/* Audio Playback */}
              <div className="mb-6">
                <audio ref={audioRef} src={audioURL} preload="auto" />
                <button
                  onClick={togglePlayback}
                  className="bg-transparent border border-surface-border-light hover:border-surface-border transition px-6 py-3 
                    rounded-full flex items-center gap-2 mx-auto"
                >
                  {isPlaying ? (
                    <Pause className="w-5 h-5 text-white" />
                  ) : (
                    <Play className="w-5 h-5 text-white" />
                  )}
                  <span>{isPlaying ? 'Pause' : 'Preview'}</span>
                </button>
              </div>

              {/* Label Input */}
              <div className="mb-6">
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Add a label (e.g., Guitar riff for verse)"
                  className="w-full bg-transparent border border-surface-border-light px-4 py-3 
                    text-white placeholder-text-subtle focus:outline-none focus:border-brand rounded-pill"
                  
                />
              </div>

              {/* Song Selection */}
              {showSongSelect ? (
                <div className="card-3d rounded-card p-4 mb-4 max-h-48 overflow-y-auto">
                  <p className="text-sm font-semibold mb-3 text-left">Select a song:</p>
                  {songs.length === 0 ? (
                    <p className="text-text-subtle text-sm">No songs yet. Save to Ideas first.</p>
                  ) : (
                    <div className="space-y-2">
                      {songs.map(song => (
                        <button
                          key={song.id}
                          onClick={() => setSelectedSongId(song.id)}
                          className={`w-full text-left px-3 py-2 rounded transition flex items-center gap-2 ${
                            selectedSongId === song.id
                              ? 'bg-brand text-white rounded-card-btn'
                              : 'bg-transparent border border-surface-border-light hover:border-surface-border text-white rounded-card-btn'
                          }`}
                        >
                          <Music className="w-4 h-4" />
                          {song.name}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => {
                        setShowSongSelect(false);
                        setSelectedSongId(null);
                      }}
                      className="flex-1 bg-transparent border border-surface-border-light hover:border-surface-border transition px-4 py-2 rounded-pill 
                        font-semibold text-sm"
                      
                    >
                      Cancel
                    </button>
                    <button
                      onClick={addToSong}
                      disabled={!selectedSongId || saving}
                      className="flex-1 bg-brand hover:bg-brand-hover 
                        disabled:opacity-disabled disabled:cursor-not-allowed 
                        transition px-4 py-2 font-semibold text-sm rounded-pill"
                      
                    >
                      {saving ? 'Saving...' : 'Add'}
                    </button>
                  </div>
                </div>
              ) : (
                /* Save Options */
                <div className="space-y-3">
                  {!canSaveIdea && (
                    <div className="bg-yellow-900/50 rounded-card px-4 py-3 mb-2">
                      <p className="text-yellow-200 text-sm text-center">
                        Free limit of {FREE_IDEAS_LIMIT} ideas reached. Add to a song instead, or upgrade to Premium.
                      </p>
                    </div>
                  )}
                  <button
                    onClick={saveToIdeas}
                    disabled={saving || !canSaveIdea}
                    className="w-full bg-brand hover:bg-brand/80 
                      disabled:opacity-disabled disabled:cursor-not-allowed 
                      transition h-btn font-semibold flex items-center justify-center gap-2 rounded-pill"
                    
                  >
                    {saving ? 'Saving...' : 'Save to Ideas'}
                  </button>

                  <button
                    onClick={() => setShowSongSelect(true)}
                    disabled={saving || songs.length === 0}
                    className="w-full bg-transparent border border-surface-border-light hover:border-surface-border 
                      disabled:opacity-disabled disabled:cursor-not-allowed 
                      transition h-btn font-semibold flex items-center justify-center gap-2 rounded-pill"
                    
                  >
                    <Music className="w-4 h-4" />
                    Add to Song
                  </button>

                  <button
                    onClick={() => {
                      resetRecording();
                    }}
                    disabled={saving}
                    className="w-full text-text-subtle hover:text-white transition py-2 text-sm"
                  >
                    Record Again
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
